/**
 * RispostaQuestionarioAccordion — una risposta a un questionario nella scheda del lead: domanda
 * → risposta in parole (etichette, non codici), i contatti lasciati, il consenso col testo
 * esatto che la persona ha visto, lo stato (una risposta sostituita da un invio più recente o
 * ritirata da chi è uscito resta in archivio, ma non conta).
 *
 * Il testo delle domande viene dalla definizione di oggi; se una domanda o un'opzione è stata
 * tolta, dall'istantanea salvata con la risposta.
 */

import React, { useMemo } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Typography } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import {
  etichettaProvenienza, normalizzaQuestionario, valoreLeggibile, type Domanda, type RispostaSalvata,
} from '@shared/questionari';
import { tokens } from '../../theme';
import { SoftChip } from '../SoftChip';
import type { PlatformQuestionnaire, PlatformQuestionnaireResponse } from '../../types/platformCrm';
import { formatDateTime, formatDurata, formatRispostaValore, nomeLingua, STATO_RISPOSTA_LABELS } from './labels';

function testoSintesi(sintesi: unknown): string | null {
  if (!sintesi) return null;
  if (typeof sintesi === 'string') return sintesi;
  try {
    return JSON.stringify(sintesi, null, 2);
  } catch {
    return null;
  }
}

export const RispostaQuestionarioAccordion: React.FC<{
  response: PlatformQuestionnaireResponse;
  questionnaire?: PlatformQuestionnaire;
}> = ({ response, questionnaire }) => {
  const domande = useMemo(() => (questionnaire ? normalizzaQuestionario(questionnaire).domande : []), [questionnaire]);

  const riga = (r: RispostaSalvata): { domanda: string; valore: string; tolta: boolean } => {
    const d = domande.find((x) => x.id === r.domanda_id);
    if (d) return { domanda: d.testo, valore: valoreLeggibile(d, r.valore, r.dettaglio), tolta: false };
    const ist = (response.istantanea_domande ?? []).find((x) => x.id === r.domanda_id);
    if (ist) {
      const comeDomanda: Pick<Domanda, 'tipo' | 'opzioni'> = { tipo: ist.tipo, opzioni: ist.opzioni };
      return { domanda: ist.testo, valore: valoreLeggibile(comeDomanda, r.valore, r.dettaglio), tolta: true };
    }
    const valore = formatRispostaValore(r.valore);
    return { domanda: r.domanda_id, valore: r.dettaglio ? `${valore} (${r.dettaglio})` : valore, tolta: true };
  };

  const stato = response.stato ?? 'attuale';
  const contatti = Object.entries(response.contatti ?? {}).filter(([, v]) => !!v);
  const sintesi = testoSintesi(response.sintesi_ai);

  return (
    <Accordion variant="outlined" disableGutters sx={{ borderColor: tokens.color.line }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={600} noWrap>
              {questionnaire?.titolo ?? response.titolo_questionario ?? response.questionnaire_id}
            </Typography>
            {stato !== 'attuale' && <SoftChip label={stato === 'ritirata' ? 'Ritirata' : 'Sostituita'} tone="default" outlined />}
          </Stack>
          <Typography variant="caption" sx={{ color: tokens.color.mist }}>
            {formatDateTime(response.completato_at)} · durata {formatDurata(response.durata_secondi)}
            {response.lingua ? ` · ${nomeLingua(response.lingua).toLowerCase()}` : ''}
            {response.provenienza ? ` · da ${etichettaProvenienza(response.provenienza)}` : ''}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Stack spacing={1.25}>
          {stato !== 'attuale' && (
            <Typography variant="caption" sx={{ color: tokens.color.ash }}>{STATO_RISPOSTA_LABELS[stato]}: non conta nelle statistiche.</Typography>
          )}
          {(response.risposte ?? []).map((r) => {
            const { domanda, valore, tolta } = riga(r);
            return (
              <Box key={r.domanda_id}>
                <Typography variant="caption" sx={{ color: tokens.color.ash, fontWeight: 600 }}>
                  {domanda}{tolta ? ' (domanda non più nel questionario)' : ''}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{valore}</Typography>
              </Box>
            );
          })}
          {contatti.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ color: tokens.color.ash, fontWeight: 600 }}>Contatti lasciati</Typography>
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{contatti.map(([, v]) => v).join(' · ')}</Typography>
            </Box>
          )}
          {response.anagrafica && (
            <Box>
              <Typography variant="caption" sx={{ color: tokens.color.ash, fontWeight: 600 }}>Anagrafica dichiarata</Typography>
              <Typography variant="body2">
                {[response.anagrafica.ragione_sociale, response.anagrafica.partita_iva, response.anagrafica.email].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
          )}
          {response.consenso && (
            <Box sx={{ p: 1.25, bgcolor: tokens.color.bone, borderRadius: 1 }}>
              <Typography variant="caption" sx={{ color: tokens.color.ash, fontWeight: 600 }}>
                Consenso agli aggiornamenti spuntato · versione {response.consenso.versione} · {nomeLingua(response.consenso.lingua).toLowerCase()}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>«{response.consenso.testo}»</Typography>
            </Box>
          )}
          {sintesi && (
            <Box sx={{ p: 1.5, bgcolor: tokens.color.bone, borderRadius: 1 }}>
              <Typography variant="caption" sx={{ color: tokens.color.ash, fontWeight: 600 }}>Sintesi AI</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{sintesi}</Typography>
            </Box>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
